// ObservabilityProvider — связывает auth-сессию с PostHog identify + Hawk
// affected-user (ТЗ §5.6). Монтируется ВНУТРИ AuthProvider (нужен useAuth)
// и под RouterProvider не требуется (identify-эффект на user-changes).
//
// PII (ТЗ §5.12): identify только UUID + low-cardinality (tier). email/имя
// НЕ передаём — server-side identify (event-bus auth/login-completed)
// добавит расширенные traits без PII. На logout — reset.
import { type ReactNode, useEffect, useRef, useSyncExternalStore } from "react";
import {
	applyServerConsent,
	getConsentState,
	noteMetaRegistration,
	onConsentChange,
	resetConsentForSignOut,
	setAnalyticsUserId,
	setConsentAgeGroup,
	setMetaAgeGroup,
} from "@/lib/ecosystem-analytics";
import { setHawkUser } from "@/lib/observability/hawk-client";
import { identifyUser, resetPostHog } from "@/lib/observability/posthog-client";
import { useAuth } from "@/providers/auth-provider";
import { useSessionStore } from "@/stores/session";

/** Решение о согласии на аналитику — реактивно (веб: баннер, приложение: сервер). */
function useAnalyticsGranted(): boolean {
	return useSyncExternalStore(
		onConsentChange,
		() => getConsentState().analytics === "granted",
		() => false,
	);
}

export function ObservabilityProvider({ children }: { children: ReactNode }): ReactNode {
	const { user } = useAuth();
	const tier = useSessionStore((s) => s.tier);
	const prevUserId = useRef<string | null>(null);
	const analyticsGranted = useAnalyticsGranted();

	// Единое согласие экосистемы IEC (OXEM-604). В приложении решение приходит с
	// сервера: зеркало IEC ID в `auth.me` (`analyticsOptOut` / `adsOptOut`); живое
	// чтение и экран выбора — `NativeConsentGate`. `undefined` — «не знаем»
	// (офлайн-синтетический пользователь), прежнее решение не трогаем. На вебе
	// вызов ничего не делает: там решение читается из `consent.js`.
	const analyticsOptOut = user?.analyticsOptOut;
	const adsOptOut = user?.adsOptOut;
	const signedIn = user !== null && user !== undefined;
	// Возраст — второй гейт Meta App Events и рекламных сигналов Firebase
	// (реклама И 18+, OXEM-647 / OXEM-172). Ставится
	// РАНЬШЕ решения о согласии: иначе на входе нового человека согласие могло бы
	// встретиться с возрастом прежнего. Офлайн-синтетический пользователь
	// зеркала не несёт (`adsOptOut === undefined`) — его `ageGroup: null` не
	// знание, а заглушка, поэтому прежнее значение не трогаем.
	const ageGroup = user?.ageGroup;
	useEffect(() => {
		if (!signedIn) return;
		const knownAge = adsOptOut === undefined ? undefined : ageGroup;
		setMetaAgeGroup(knownAge);
		setConsentAgeGroup(knownAge);
		applyServerConsent({ analyticsOptOut, adsOptOut });
	}, [signedIn, analyticsOptOut, adsOptOut, ageGroup]);

	// Регистрация в Meta App Events (OXEM-864): сервер говорит, что аккаунт создан
	// этим входом; отправка — один раз и только когда Meta разрешена (реклама И
	// 18+), до решения регистрация ждёт. Выход или погасший признак — `null`.
	const registeredUserId = user?.justRegistered === true ? user.id : null;
	useEffect(() => {
		noteMetaRegistration(registeredUserId);
	}, [registeredUserId]);

	// `user_id` в GA4 / Firebase — только при согласии на аналитику: его могли
	// дать уже после входа (тогда ставим) или отозвать (тогда снимаем).
	// У вошедшего `analyticsUid === undefined` — тоже «не знаем» (офлайн): тогда
	// `user_id` не трогаем, иначе офлайн-события приложения ушли бы без склейки.
	const analyticsUid = user ? user.analyticsUid : null;
	useEffect(() => {
		if (analyticsUid !== undefined) setAnalyticsUserId(analyticsGranted ? analyticsUid : null);
	}, [analyticsUid, analyticsGranted]);

	useEffect(() => {
		const userId = user?.id ?? null;
		if (userId && userId !== prevUserId.current) {
			// Hawk знает пользователя всегда — это диагностика сбоев, а не
			// аналитика, и под cookie-категорию `analytics` она не подпадает.
			setHawkUser(userId);
			prevUserId.current = userId;
		} else if (!userId && prevUserId.current) {
			// Logout — обнулить distinctId / user; в приложении решение было про
			// этого человека, следующий вход прочитает своё.
			resetPostHog();
			setHawkUser(null);
			resetConsentForSignOut();
			setMetaAgeGroup(null);
			prevUserId.current = null;
		}
	}, [user?.id]);

	// Identify в PostHog: только UUID + tier (low-cardinality), и только при
	// согласии — иначе первый же `identify` создал бы персону человеку, который
	// сбор не разрешал. Расширенные traits идут server-side.
	useEffect(() => {
		if (user?.id && analyticsGranted) identifyUser(user.id, { subscriptionTier: tier });
	}, [user?.id, tier, analyticsGranted]);

	return children;
}
